import { useEffect, useState } from "react"
import Modal from "~app/Modal"
import { WizardColumn, WizardRow } from "~styled/wizard"
import ControlSelect from "~app/ControlSelect"
import { useForm } from "react-hook-form"
import { createDropdownCollection } from "~utils/dropdown"
import {
  PERK_REDEMPTION_STATUS,
  REDEMPTION_STATUS_TO_USER_FACING,
} from "~constants/status"
import Input from "~app/Input"
import {
  DateRange,
  getLast14daysRangeISO,
  getLast28daysRangeISO,
  getLast7daysRangeISO,
} from "~utils/utils"
import DatePicker from "~app/DatePicker"
import { filtersProps } from "~components/dashboard/RedemptionWidget"
import moment from "moment"
import { ButtonGroup } from "~styled/button"
import styled from "styled-components"
import colors from "~constants/colors"

interface ModalProps {
  isOpen: boolean
  onSearch: (v) => void
  onClose: (v) => void
  data: filtersProps
}

const ModalFilter = ({ isOpen, onSearch, onClose, data }: ModalProps) => {
  const [redemptionDate, setRedemptionDate] = useState<string | [Date, Date]>(
    "",
  )
  const [sailDate, setSailDate] = useState<string | [Date, Date]>("")
  const [predefinedRangeDateSelected, setPredefinedRangeDateSelected] =
    useState("")
  const dateRangeKeys = Object.keys(DateRange) as (keyof typeof DateRange)[]

  const DateRangeMap = {
    [DateRange.LAST_7_DAYS]: getLast7daysRangeISO(),
    [DateRange.LAST_14_DAYS]: getLast14daysRangeISO(),
    [DateRange.LAST_28_DAYS]: getLast28daysRangeISO(),
  }

  const {
    handleSubmit,
    register,
    control,
    getValues,
    setValue,
    unregister,
    reset,
  } = useForm()

  const perkRedemptionStatusList = createDropdownCollection(
    [
      {
        label:
          REDEMPTION_STATUS_TO_USER_FACING[
            PERK_REDEMPTION_STATUS.PENDING_EXTERNAL_MANUAL_VALIDATION
          ],
        value: PERK_REDEMPTION_STATUS.PENDING_EXTERNAL_MANUAL_VALIDATION,
      },
      {
        label:
          REDEMPTION_STATUS_TO_USER_FACING[
            PERK_REDEMPTION_STATUS.PENDING_EXTERNAL_VALIDATION
          ],
        value: PERK_REDEMPTION_STATUS.PENDING_EXTERNAL_VALIDATION,
      },
      {
        label:
          REDEMPTION_STATUS_TO_USER_FACING[PERK_REDEMPTION_STATUS.REDEEMED],
        value: PERK_REDEMPTION_STATUS.REDEEMED,
      },
      {
        label:
          REDEMPTION_STATUS_TO_USER_FACING[PERK_REDEMPTION_STATUS.INVALIDATED],
        value: PERK_REDEMPTION_STATUS.INVALIDATED,
      },
    ],
    "label",
    "value",
  )

  const submit = formData => {
    onSearch(formData)
    onClose(false)
  }

  useEffect(() => {
    if (isOpen) {
      reset({
        status: {
          label: REDEMPTION_STATUS_TO_USER_FACING[data.status],
          value: data.status,
        },
        bookingId: data.bookingId,
        bookingLastName: data.bookingLastName,
        bookingFirstName: data.bookingFirstName,
        userEmail: data.userEmail,
        userId: data.userId,
        proofOfOwnershipName: data.proofOfOwnershipName,
        updatedByEmail: data.updatedByEmail,
      })
      if (!!data.createdAtFrom && !!data.createdAtTo) {
        const dates: [Date, Date] = [
          moment(data.createdAtFrom).toDate(),
          moment(data.createdAtTo).toDate(),
        ]
        setRedemptionDate(dates)
        checkRedemptionDateRange(dates)
      } else {
        setRedemptionDate("")
        setPredefinedRangeDateSelected("")
      }
      if (data.bookingEventDateFrom && data.bookingEventDateTo) {
        setSailDate([
          moment(data.bookingEventDateFrom).toDate(),
          moment(data.bookingEventDateTo).toDate(),
        ])
      } else {
        setSailDate("")
      }
    }
  }, [isOpen])

  const checkRedemptionDateRange = (date: [Date, Date]) => {
    if (!!date[0] && date[1]) {
      const formatedDate = [
        moment(date[0]).format("YYYY-MM-DD"),
        moment(date[1]).format("YYYY-MM-DD"),
      ]

      switch (JSON.stringify(formatedDate)) {
        case JSON.stringify(DateRangeMap[DateRange.LAST_7_DAYS]):
          setPredefinedRangeDateSelected(DateRange.LAST_7_DAYS)
          break
        case JSON.stringify(DateRangeMap[DateRange.LAST_14_DAYS]):
          setPredefinedRangeDateSelected(DateRange.LAST_14_DAYS)
          break
        case JSON.stringify(DateRangeMap[DateRange.LAST_28_DAYS]):
          setPredefinedRangeDateSelected(DateRange.LAST_28_DAYS)
          break
        default:
          setPredefinedRangeDateSelected("")
      }
    } else {
      setPredefinedRangeDateSelected("")
    }
  }

  const changeRedemptionRangeDate = value => {
    if (!!DateRangeMap[value]) {
      const dates = [
        moment(DateRangeMap[value][0]).toDate(),
        moment(DateRangeMap[value][1]).toDate(),
      ]
      setPredefinedRangeDateSelected(value)
      setRedemptionDate(dates)
      setValue("redemptionDate", dates)
    } else {
      setPredefinedRangeDateSelected("")
      setRedemptionDate("")
      setValue("redemptionDate", "")
    }
  }

  return (
    <Modal
      cancelButton={true}
      isOpen={isOpen}
      buttonLabel={"Search"}
      onRequestClose={() => {}}
      onRequestConfirm={handleSubmit(submit)}
      onRequestCancel={() => onClose(false)}
    >
      <WizardRow>
        <WizardColumn>
          <Input
            name="bookingId"
            placeholder=""
            label="Booking number"
            register={register("bookingId", { required: false })}
          />
        </WizardColumn>
        <WizardColumn>
          <Input
            name="bookingFirstName"
            placeholder=""
            label="Booking First Name"
            register={register("bookingFirstName", { required: false })}
          />
        </WizardColumn>
        <WizardColumn>
          <Input
            name="bookingLastName"
            placeholder=""
            label="Booking Last Name"
            register={register("bookingLastName", { required: false })}
          />
        </WizardColumn>
      </WizardRow>

      <WizardRow>
        <WizardColumn>
          <Input
            name="userId"
            placeholder=""
            label="User ID"
            register={register("userId", { required: false })}
          />
        </WizardColumn>
        <WizardColumn>
          <Input
            name="userEmail"
            placeholder=""
            label="User Email"
            register={register("userEmail", { required: false })}
          />
        </WizardColumn>
        <WizardColumn>
          <Input
            name="proofOfOwnershipName"
            placeholder=""
            label="Broker Statement Name"
            register={register("proofOfOwnershipName", { required: false })}
          />
        </WizardColumn>
      </WizardRow>
      <WizardRow>
        <WizardColumn>
          <div style={{}}>
            <DatePicker
              label={"Redemption date"}
              name={"redemptionDate"}
              setValue={setValue}
              getValues={getValues}
              unregister={unregister}
              register={register}
              selectsRange={true}
              placeholder={"Select date range"}
              value={redemptionDate}
              isClearable={true}
              checkDateRange={checkRedemptionDateRange}
            />

            <ButtonGroup>
              <span>Predefined date range: Last</span>

              {dateRangeKeys.map(key => (
                <FilterButton
                  key={key}
                  className={
                    DateRange[key] === predefinedRangeDateSelected
                      ? "active"
                      : ""
                  }
                  onClick={() => changeRedemptionRangeDate(DateRange[key])}
                >
                  {DateRange[key]}
                </FilterButton>
              ))}
              <span>days</span>
            </ButtonGroup>
          </div>
        </WizardColumn>

        <WizardColumn>
          <ControlSelect
            name={"status"}
            label={"Redemption status"}
            control={control}
            options={perkRedemptionStatusList}
            rules={{ required: false }}
            isClearable={true}
          />
        </WizardColumn>

        <WizardColumn>
          <DatePicker
            label={"Sail Date"}
            name={"sailDate"}
            setValue={setValue}
            getValues={getValues}
            unregister={unregister}
            register={register}
            selectsRange={true}
            placeholder={"Select date range"}
            isClearable={true}
            value={sailDate}
          />
        </WizardColumn>
      </WizardRow>
    </Modal>
  )
}

const FilterButton = styled.button`
  background-color: ${colors.gray1};
  font-weight: bold;
  height: 32px;
  width: 34px;
  border-radius: 6px;
  border-color: ${colors.gray1};
  cursor: pointer;
  color: ${colors.black};

  &.active {
    background-color: ${colors.gray2};
    color: ${colors.black};
    border-color: ${colors.blue1};
  }
`

export default ModalFilter
